import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { FileText, CreditCard, History, Sparkles, Gauge } from "lucide-react";
import { CVBuilder } from "@/components/cv/CVBuilder";
import { AdminAtsScanner } from "@/components/cv/AdminAtsScanner";
import { CvPurchasePanel } from "@/components/cv/CvPurchasePanel";
import { CvEntitlementPanel } from "@/components/cv/CvEntitlementPanel";
import { CvActivityLog } from "@/components/cv/CvActivityLog";
import { Disclaimer } from "@/components/site/Disclaimer";
import { OnboardingChecklistCard } from "@/components/cv/OnboardingChecklistCard";

export const Route = createFileRoute("/_authenticated/portal/cv")({
  validateSearch: (s: Record<string, unknown>) => ({
    template: typeof s.template === "string" ? s.template : undefined,
  }),
  component: CVBuilderPage,
});

type Tab = "builder" | "ats" | "credits" | "activity";

function CVBuilderPage() {
  const { template } = Route.useSearch();
  const [tab, setTab] = useState<Tab>("builder");

  const tabs: { id: Tab; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
    { id: "builder", label: "Build CV", icon: FileText },
    { id: "ats", label: "ATS scan & revamp", icon: Gauge },
    { id: "credits", label: "Credits & Billing", icon: CreditCard },
    { id: "activity", label: "Activity", icon: History },
  ];


  return (
    <div className="container-narrow space-y-6 py-6">
      {/* Page header */}
      <header className="rounded-2xl border border-gold/40 bg-gradient-to-br from-gold/10 via-background to-background px-5 py-5 shadow-soft">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-gold">
              <Sparkles className="h-3.5 w-3.5" /> AI CV Builder
            </p>
            <h1 className="font-display text-2xl text-navy sm:text-3xl">
              Build, preview &amp; download your CV
            </h1>
            <p className="mt-1 max-w-xl text-sm text-muted-foreground">
              Craft a recruiter-ready, ATS-friendly CV in minutes. Manage your
              credits and review past generations from one place.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setTab("credits")}
            className="inline-flex shrink-0 items-center justify-center gap-1.5 rounded-md bg-navy px-4 py-2 text-sm font-semibold text-navy-foreground shadow-sm hover:brightness-110"
          >
            <CreditCard className="h-4 w-4" /> Buy CV credits
          </button>
        </div>
      </header>

      <Disclaimer variant="compact" />

      {/* Tab navigation */}
      <nav
        role="tablist"
        aria-label="CV Builder sections"
        className="sticky top-0 z-10 -mx-2 flex gap-1 overflow-x-auto rounded-xl border border-border bg-card/95 p-1.5 shadow-soft backdrop-blur"
      >
        {tabs.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              role="tab"
              aria-selected={active}
              onClick={() => setTab(t.id)}
              className={`inline-flex flex-1 min-w-fit items-center justify-center gap-2 whitespace-nowrap rounded-lg px-4 py-2 text-sm font-medium transition ${
                active
                  ? "bg-navy text-navy-foreground shadow-sm"
                  : "text-foreground/70 hover:bg-muted"
              }`}
            >
              <Icon className="h-4 w-4" /> {t.label}
            </button>
          );
        })}
      </nav>

      {/* Tab panels */}
      {tab === "builder" && (
        <div className="space-y-4">
          <OnboardingChecklistCard />
          <CvEntitlementPanel />
          <CVBuilder presetTemplate={template} />
        </div>
      )}


      {tab === "ats" && (
        <div className="space-y-4">
          <AdminAtsScanner
            eyebrow="ATS scanner & AI revamp"
            title="Test your CV against Applicant Tracking Systems"
            subhead="Upload your existing CV (.pdf, .docx or .txt) or paste the text, add the role you're applying for and — ideally — the job advert. You'll get a 0–100 ATS score, a ranked list of fixes, a keyword gap analysis, and a one-click AI revamp that rewrites your CV for a higher score using only facts already in your CV."
          />
        </div>
      )}



      {tab === "credits" && (
        <div className="space-y-4">
          <CvEntitlementPanel />
          <CvPurchasePanel />
        </div>
      )}

      {tab === "activity" && (
        <div className="space-y-4">
          <CvActivityLog />
        </div>
      )}
    </div>
  );
}
